import { useState } from 'react';
import Today from './views/Today';
import Portfolio from './views/Portfolio';
import Watchlist from './views/Watchlist';
import ThresholdPanel from './components/ThresholdPanel';
import DeepDiveModal from './components/DeepDiveModal';
import AboutModal from './components/AboutModal';
import { DEFAULT_THRESHOLDS } from './rules/types';
import type { Thresholds } from './rules/types';

type Tab = 'today' | 'portfolio' | 'watchlist';

const TABS: { key: Tab; label: string; blurb: string }[] = [
  { key: 'today', label: 'Today',
    blurb: 'Every instrument, with the stance each rule produces and why.' },
  { key: 'portfolio', label: 'Portfolio',
    blurb: 'Average-cost positions, weights, and P&L against two distinct baselines.' },
  { key: 'watchlist', label: 'Watchlist',
    blurb: 'Candidates broken down condition by condition, so a verdict is never a black box.' },
];

export default function App() {
  const [tab, setTab] = useState<Tab>('today');
  // Thresholds are held HERE, not per-view, because Today and Watchlist are both downstream of
  // the same rules. Duplicating them per screen would let them drift and misrepresent the app,
  // where they live once in Settings.
  const [thresholds, setThresholds] = useState<Thresholds>(DEFAULT_THRESHOLDS);
  const [deepDive, setDeepDive] = useState<string | null>(null);
  const [showAbout, setShowAbout] = useState<boolean>(false);

  const active = TABS.find((x) => x.key === tab)!;

  return (
    <div className="wrap">
      <div className="masthead" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1>StokApp</h1>
          <span className="sub">interactive demo — the rule engine, running live in your browser</span>
        </div>
        <button
          onClick={() => setShowAbout(true)}
          style={{
            marginLeft: 'auto',
            background: 'rgba(99, 102, 241, 0.15)',
            border: '1px solid rgba(99, 102, 241, 0.4)',
            color: '#818cf8',
            borderRadius: '9999px',
            padding: '6px 14px',
            fontSize: '0.8rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span>💡 How to Use & Get Full App</span>
        </button>
      </div>

      <div className="disclaimer">
        <strong>Everything here is fictional.</strong> These companies do not exist and the prices
        are generated. Nothing on this page is investment advice, and no real market data is
        shown. The point is to demonstrate <em>how the rules behave</em>, not what any security
        is worth.
      </div>

      {/* The three screens, presented as a segmented control so it's immediately obvious
          that there's more here than the first page. `aria-selected` carries the same
          information to screen readers that the fill carries visually. */}
      <nav className="tabs" role="tablist" aria-label="Demo screens">
        {TABS.map((x) => (
          <button
            key={x.key}
            role="tab"
            aria-selected={tab === x.key}
            className={`tab ${tab === x.key ? 'active' : ''}`}
            onClick={() => setTab(x.key)}
          >
            {x.label}
          </button>
        ))}
      </nav>
      <p className="tabBlurb">{active.blurb}</p>

      {/* Shown on the two screens the thresholds actually drive. Portfolio is a record of what
          was bought, not a rule output, so putting the controls there would imply they change
          it — they don't. */}
      {tab !== 'portfolio' && (
        <ThresholdPanel thresholds={thresholds} onChange={setThresholds} />
      )}

      {tab === 'today' && <Today thresholds={thresholds} onOpenDeepDive={setDeepDive} />}
      {tab === 'portfolio' && <Portfolio onOpenDeepDive={setDeepDive} />}
      {tab === 'watchlist' && <Watchlist thresholds={thresholds} onOpenDeepDive={setDeepDive} />}

      {deepDive && <DeepDiveModal ticker={deepDive} onClose={() => setDeepDive(null)} />}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      <footer>
        StokApp is a Mac app for tracking a personal portfolio against rules you define.{' '}
        <a href="https://ponyapp.net">Back to ponyapp.net</a>
      </footer>
    </div>
  );
}
